<template>
  <v-row>
    <v-col cols="12" v-if="loading && !settings">
      <v-skeleton-loader type="table" />
    </v-col>
    <v-col cols="12" v-else-if="settings">
      <v-card class="mb-6">
        <v-toolbar color="transparent" flat height="55">
          <v-toolbar-title class="text--secondary subtitle-1 font-weight-medium">Notificaciones</v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <v-row align="center" justify="center" class="mr-1">
              <span class="mr-4 d-block body-2 text--primary">Silenciar todas mis notificaciones</span>
              <v-switch v-model="editable.muted" class="py-0 my-0" :width="32" :height="18" hide-details />
            </v-row>
          </v-toolbar-items>
        </v-toolbar>
        <v-divider />
        <v-card-text class="pa-0 background">
          <v-list class="transparent">
            <v-list-item two-line>
              <v-list-item-content>
                <v-list-item-title class="body-2 text--secondary font-weight-medium">Frecuencia del resumen por correo</v-list-item-title>
                <v-list-item-subtitle class="mt-1">
                  Los correos se agrupan en un resumen en lugar de enviarse uno por evento. Las notificaciones dentro de la app llegan al instante.
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action style="min-width: 220px">
                <v-select v-model="editable.email_digest" :items="digestOptions" :disabled="editable.muted" item-text="label" item-value="value" hide-details dense outlined />
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card-text>
      </v-card>

      <v-card v-for="group in groups" :key="group.name" class="mb-6">
        <v-toolbar color="transparent" flat height="55">
          <v-toolbar-title class="text--secondary subtitle-1 font-weight-medium">{{group.name}}</v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <v-row align="center" justify="end" class="mr-1">
              <span class="d-block caption text--secondary" style="width: 88px; text-align: right">Dashboard</span>
              <span class="d-block caption text--secondary" style="width: 88px; text-align: right">Email</span>
            </v-row>
          </v-toolbar-items>
        </v-toolbar>
        <v-divider />
        <v-card-text class="pa-0 background">
          <v-list class="transparent">
            <template v-for="(preference, index) in group.preferences">
              <v-list-item two-line :key="`item-${preference.event_type}`">
                <v-list-item-content>
                  <v-list-item-title class="body-2 text--secondary font-weight-medium">
                    {{preference.label}}
                    <v-chip class="ml-2" v-if="preference.is_overridden" color="lightBlue" text-color="primary" x-small>Personalizado</v-chip>
                  </v-list-item-title>
                  <v-list-item-subtitle class="mt-1">{{preference.description}}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action style="width: 88px" class="justify-end">
                  <v-switch v-model="preference.inapp" :disabled="editable.muted" :width="32" :height="18" hide-details />
                </v-list-item-action>
                <v-list-item-action style="width: 88px" class="justify-end">
                  <v-switch v-model="preference.email" :disabled="editable.muted || editable.email_digest === 'off'" :width="32" :height="18" hide-details />
                </v-list-item-action>
              </v-list-item>
              <v-divider :key="`divider-${preference.event_type}`" v-if="index < group.preferences.length - 1" />
            </template>
          </v-list>
        </v-card-text>
      </v-card>

      <v-row class="mx-0" align="center">
        <v-col class="px-0">
          <span class="caption text--secondary" v-if="editable.muted">Con el silencio activado no recibirás ninguna notificación en esta organización.</span>
          <span class="caption text--secondary" v-else-if="editable.email_digest === 'off'">Con el resumen desactivado solo recibirás notificaciones dentro de la app.</span>
        </v-col>
        <v-col class="px-0 text-right" cols="auto">
          <v-btn class="mr-2" @click="reset" outlined :disabled="!hasChanges || saving">Descartar</v-btn>
          <v-btn @click="save" :loading="saving" :disabled="!hasChanges" color="primary">Guardar</v-btn>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>
<script>
import { mapState } from 'vuex'
import cloneDeep from 'lodash/cloneDeep'
import isEqual from 'lodash/isEqual'
import GenericViewMixin from '@/mixins/GenericViewMixin'

const DIGEST_OPTIONS = [
  { value: 'every_30_min', label: 'Cada 30 minutos' },
  { value: 'daily', label: 'Una vez al día' },
  { value: 'off', label: 'No enviar correos' }
]

/**
 * Personal notification preferences for the signed-in member in the active
 * workspace.
 *
 * The whole matrix is rendered from the API response: the backend merges its
 * catalog with the member's overrides and returns labels, descriptions, groups
 * and effective values, so adding a notification type on the server needs no
 * change here.
 */
export default {
  mixins: [
    GenericViewMixin
  ],
  data: () => ({
    digestOptions: DIGEST_OPTIONS,
    editable: { muted: false, email_digest: 'every_30_min', preferences: [] },
    original: null
  }),
  computed: {
    ...mapState({
      settings: state => state.notifications.settings,
      loading: state => state.notifications.loading,
      saving: state => state.notifications.saving
    }),
    groups () {
      // Preserve the catalog's order rather than sorting: it is the order the
      // backend declared the types in, most relevant first.
      const groups = []
      this.editable.preferences.forEach((preference) => {
        const name = preference.group || 'Otras'
        let group = groups.find((g) => g.name === name)
        if (!group) {
          group = { name: name, preferences: [] }
          groups.push(group)
        }
        group.preferences.push(preference)
      })
      return groups
    },
    hasChanges () {
      return !isEqual(this.editable, this.original)
    }
  },
  created () {
    this.getSettings()
  },
  watch: {
    /**
     * Switching workspace changes which member these preferences belong to.
     */
    'account.id' () {
      this.getSettings()
    }
  },
  methods: {
    getSettings () {
      return this.$store.dispatch('notifications/RETRIEVE')
      .then(() => {
        this.load()
      })
      .catch((error) => {
        this.$dialog.message.warning(this.parseErrors(error.response.data))
      })
    },
    load () {
      this.editable = cloneDeep(this.settings)
      this.original = cloneDeep(this.settings)
    },
    reset () {
      this.editable = cloneDeep(this.original)
    },
    save () {
      /**
       * Only the rows that actually moved are sent. The backend stores an
       * override solely where the member departs from the default and deletes
       * the row when they return to it, so sending the untouched ones would
       * pin them against any future change of that default.
       */
      const changed = this.editable.preferences.filter((preference) => {
        const before = this.original.preferences.find((p) => p.event_type === preference.event_type)
        return before && (before.inapp !== preference.inapp || before.email !== preference.email)
      })

      const payload = {
        muted: this.editable.muted,
        email_digest: this.editable.email_digest
      }

      if (changed.length) {
        payload.preferences = changed.map((preference) => ({
          event_type: preference.event_type,
          inapp: preference.inapp,
          email: preference.email
        }))
      }

      this.$store.dispatch('notifications/UPDATE', payload)
      .then(() => {
        this.load()
        this.$dialog.message.info('Tus preferencias de notificaciones se han actualizado')
      })
      .catch((error) => {
        this.$dialog.message.warning(this.parseErrors(error.response.data))
      })
    }
  }
}
</script>
